import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import ProfileCard from '../components/ProfileCard';
import api from '../api/axios';
import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const DISTRICTS_AP = ['Anantapur','Chittoor','East Godavari','Guntur','Krishna','Kurnool','Nellore','Prakasam','Srikakulam','Visakhapatnam','Vizianagaram','West Godavari','YSR Kadapa'];
const DISTRICTS_TS = ['Adilabad','Bhadradri Kothagudem','Hyderabad','Jagtial','Jangaon','Jayashankar Bhupalpally','Kamareddy','Karimnagar','Khammam','Kumuram Bheem','Mahabubabad','Mahabubnagar','Mancherial','Medak','Medchal','Mulugu','Nagarkurnool','Nalgonda','Narayanpet','Nirmal','Nizamabad','Peddapalli','Rajanna Sircilla','Ranga Reddy','Sangareddy','Siddipet','Suryapet','Vikarabad','Wanaparthy','Warangal Rural','Warangal Urban','Yadadri Bhuvanagiri'];

const DENOMINATIONS = [
  {value:'catholic',label:'Catholic'},{value:'csi',label:'CSI'},{value:'cni',label:'CNI'},
  {value:'pentecostal',label:'Pentecostal'},{value:'assemblies_of_god',label:'Assemblies of God'},
  {value:'seventh_day_adventist',label:'SDA'},{value:'methodist',label:'Methodist'},
  {value:'lutheran',label:'Lutheran'},{value:'brethren',label:'Brethren'},
  {value:'baptist',label:'Baptist'},{value:'church_of_god',label:'Church of God'},
  {value:'other',label:'Other'},
];

const EDUCATIONS = [
  {value:'below_10th',label:'Below 10th'},{value:'sslc',label:'SSLC'},{value:'intermediate',label:'Intermediate'},
  {value:'diploma',label:'Diploma'},{value:'bachelors',label:"Bachelor's"},{value:'masters',label:"Master's"},
  {value:'phd',label:'PhD'},{value:'other',label:'Other'},
];

const defaultFilters = { min_age:'18', max_age:'50', district:'', denomination:'', education_level:'', marital_status:'', annual_income:'', occupation_type:'' };

export default function SearchPage() {
  const [filters, setFilters]   = useState(defaultFilters);
  const [profiles, setProfiles] = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);
  const [loading,  setLoading]  = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const search = async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ ...filters, page: p, limit: 12 });
      const { data } = await api.get(`/profiles/search?${params}`);
      setProfiles(data.profiles || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(1); }, []);

  const handleFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const resetFilters = () => { setFilters(defaultFilters); };

  return (
    <div className="min-h-screen bg-cream-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-2xl font-bold text-gray-900">🔍 Search Profiles</h1>
          <button onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 btn-outline text-sm py-2">
            <AdjustmentsHorizontalIcon className="w-4 h-4" />
            Filters
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="card p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs text-gray-500 font-medium">Age From</label>
                <input type="number" min="18" max="70" className="input-field mt-1"
                  value={filters.min_age} onChange={e => handleFilter('min_age', e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Age To</label>
                <input type="number" min="18" max="70" className="input-field mt-1"
                  value={filters.max_age} onChange={e => handleFilter('max_age', e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">District</label>
                <select className="input-field mt-1" value={filters.district} onChange={e => handleFilter('district', e.target.value)}>
                  <option value="">All Districts</option>
                  <optgroup label="Andhra Pradesh">{DISTRICTS_AP.map(d => <option key={d} value={d}>{d}</option>)}</optgroup>
                  <optgroup label="Telangana">{DISTRICTS_TS.map(d => <option key={d} value={d}>{d}</option>)}</optgroup>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Denomination</label>
                <select className="input-field mt-1" value={filters.denomination} onChange={e => handleFilter('denomination', e.target.value)}>
                  <option value="">All</option>
                  {DENOMINATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Education</label>
                <select className="input-field mt-1" value={filters.education_level} onChange={e => handleFilter('education_level', e.target.value)}>
                  <option value="">Any</option>
                  {EDUCATIONS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Marital Status</label>
                <select className="input-field mt-1" value={filters.marital_status} onChange={e => handleFilter('marital_status', e.target.value)}>
                  <option value="">Any</option>
                  <option value="never_married">Never Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Occupation</label>
                <select className="input-field mt-1" value={filters.occupation_type} onChange={e => handleFilter('occupation_type', e.target.value)}>
                  <option value="">Any</option>
                  <option value="employed">Employed</option>
                  <option value="self_employed">Self Employed</option>
                  <option value="business">Business</option>
                  <option value="student">Student</option>
                  <option value="not_working">Not Working</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => search(1)} className="btn-primary flex-1 py-2 flex items-center justify-center gap-2">
                <MagnifyingGlassIcon className="w-4 h-4" /> Search
              </button>
              <button onClick={() => { resetFilters(); setTimeout(() => search(1), 100); }}
                className="btn-outline flex-1 py-2">Reset</button>
            </div>
          </div>
        )}

        {/* Results */}
        <p className="text-sm text-gray-500 mb-4">{total} profiles found</p>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-52 bg-gray-200" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-8 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-5xl mb-4">🔍</p>
            <p className="text-gray-500">No profiles found. Try adjusting your filters.</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {profiles.map(p => <ProfileCard key={p.id} profile={p} />)}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button disabled={page === 1} onClick={() => search(page - 1)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-cream-100">← Prev</button>
                <span className="text-sm text-gray-500">Page {page} of {pages}</span>
                <button disabled={page === pages} onClick={() => search(page + 1)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-cream-100">Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
